import React from "react";

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-white text-gray-800">
      <div className="bg-red-600 py-12 text-center text-white">
        <h1 className="text-3xl font-bold md:text-4xl">Return and Refund Policy</h1>
        <p className="mt-3 text-sm md:text-base">For Customers Shopping on Havbit Marketplace</p>
        <div className="mt-4 text-xs md:text-sm opacity-90">
          <p>Last Updated: May 2026 | Effective Date: May 2026 | Version: 1.0</p>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-6 py-10 md:py-12">
        <div className="mb-10 border border-gray-200 rounded-lg bg-gray-50 p-6">
          <h2 className="mb-4 text-xl font-semibold text-red-600">Table of Contents</h2>
          <ol className="list-decimal list-inside space-y-2 text-sm md:text-base">
            <li>Our Commitment and Philosophy</li>
            <li>Eligible Return Reasons</li>
            <li>Time Limit for Raising a Return Request</li>
            <li>Step-by-Step Return Request Process</li>
            <li>Products That Cannot Be Returned</li>
            <li>Refund Process and Timelines</li>
            <li>Replacement Policy</li>
            <li>Checking Your Return and Refund Status</li>
            <li>Disputes and Escalations</li>
          </ol>
        </div>

        <div className="space-y-8 text-base leading-7 text-gray-800">
          <p>
            At Havbit, we want every customer to be completely satisfied with their shopping
            experience. This Return and Refund Policy explains your rights as a customer when a
            product you receive does not meet the expected standard — whether due to damage,
            incorrect delivery, expiry, or quality issues. All products on Havbit are sold by
            independent sellers, and this Policy governs the return and refund process that Havbit
            facilitates on your behalf. Please read this Policy carefully.
          </p>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">1. Our Commitment and Philosophy</h2>
            <p>
              Havbit is dedicated exclusively to packaged food products, and food safety is our
              highest priority. We require all sellers on our platform to comply with stringent FSSAI
              food safety standards. However, we acknowledge that despite these requirements,
              issues may occasionally arise. When they do, our commitment is to ensure that you —
              the customer — are made whole, promptly and fairly. Our return and refund process is
              designed to be straightforward, evidence-based, and customer-centric, while also
              protecting legitimate sellers from fraudulent claims.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">2. Eligible Return Reasons</h2>
            <p className="mb-3">
              You are entitled to request a return and refund — or a replacement — when any of the
              following situations arise after delivery:
            </p>

            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 text-left text-sm">
                <thead className="bg-red-50 text-gray-900">
                  <tr>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Return Reason</th>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Eligible?</th>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Evidence Required</th>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Who Bears Cost?</th>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Resolution Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Product received in damaged or broken condition</td>
                    <td className="border border-gray-200 px-3 py-2">Yes</td>
                    <td className="border border-gray-200 px-3 py-2">Photo/video of damaged product</td>
                    <td className="border border-gray-200 px-3 py-2">Seller / Courier</td>
                    <td className="border border-gray-200 px-3 py-2">3–5 business days</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Wrong product or variant delivered</td>
                    <td className="border border-gray-200 px-3 py-2">Yes</td>
                    <td className="border border-gray-200 px-3 py-2">Photo of product received + order details</td>
                    <td className="border border-gray-200 px-3 py-2">Seller</td>
                    <td className="border border-gray-200 px-3 py-2">3–5 business days</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Expired product delivered</td>
                    <td className="border border-gray-200 px-3 py-2">Yes</td>
                    <td className="border border-gray-200 px-3 py-2">Clear photo of expiry date on packaging</td>
                    <td className="border border-gray-200 px-3 py-2">Seller</td>
                    <td className="border border-gray-200 px-3 py-2">3–5 business days</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Product quality significantly different from listing</td>
                    <td className="border border-gray-200 px-3 py-2">Yes</td>
                    <td className="border border-gray-200 px-3 py-2">Photo + written description of the difference</td>
                    <td className="border border-gray-200 px-3 py-2">Seller</td>
                    <td className="border border-gray-200 px-3 py-2">5–7 business days</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Items missing from multi-product order</td>
                    <td className="border border-gray-200 px-3 py-2">Yes</td>
                    <td className="border border-gray-200 px-3 py-2">Photo of received package + packing slip</td>
                    <td className="border border-gray-200 px-3 py-2">Seller</td>
                    <td className="border border-gray-200 px-3 py-2">3–5 business days</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Food safety concern with product</td>
                    <td className="border border-gray-200 px-3 py-2">Yes</td>
                    <td className="border border-gray-200 px-3 py-2">Photo + description of the concern</td>
                    <td className="border border-gray-200 px-3 py-2">Seller</td>
                    <td className="border border-gray-200 px-3 py-2">3–5 business days</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Changed mind after placing order</td>
                    <td className="border border-gray-200 px-3 py-2">No</td>
                    <td className="border border-gray-200 px-3 py-2">N/A</td>
                    <td className="border border-gray-200 px-3 py-2">N/A</td>
                    <td className="border border-gray-200 px-3 py-2">Not eligible</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Opened and partially used product</td>
                    <td className="border border-gray-200 px-3 py-2">No</td>
                    <td className="border border-gray-200 px-3 py-2">N/A</td>
                    <td className="border border-gray-200 px-3 py-2">N/A</td>
                    <td className="border border-gray-200 px-3 py-2">Not eligible</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Seal broken by customer (non-safety concern)</td>
                    <td className="border border-gray-200 px-3 py-2">No</td>
                    <td className="border border-gray-200 px-3 py-2">N/A</td>
                    <td className="border border-gray-200 px-3 py-2">N/A</td>
                    <td className="border border-gray-200 px-3 py-2">Not eligible</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Request after 48-hour delivery window</td>
                    <td className="border border-gray-200 px-3 py-2">No</td>
                    <td className="border border-gray-200 px-3 py-2">N/A</td>
                    <td className="border border-gray-200 px-3 py-2">N/A</td>
                    <td className="border border-gray-200 px-3 py-2">Not eligible</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">3. Time Limit for Raising a Return Request</h2>
            <p>
              Return requests must be raised within 48 hours of the delivery confirmation. The
              48-hour window begins from the time the courier partner marks your order as “Delivered”
              in the tracking system — even if you personally collected the package later. This time
              limit exists because food products are perishable and conditions can change rapidly after
              delivery.
            </p>
            <p className="mt-3">
              We make an exception to the 48-hour window in the following circumstances: where a
              genuine food safety concern is identified — such as finding a foreign object in the
              product, or experiencing an adverse reaction — which may be reported up to 7 days after
              delivery. In such cases, please contact us immediately at hello@havbit.in.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">4. Step-by-Step Return Request Process</h2>

            <h3 className="mb-2 text-lg font-semibold text-gray-900">Step 1 — Check Eligibility and Gather Evidence</h3>
            <p>
              Before raising a return request, confirm that your return reason falls within the eligible
              categories in Section 2 and that you are within the 48-hour window. Then gather the
              required evidence — take clear, well-lit photographs or a short video showing the issue.
              For an expired product, photograph the expiry date clearly. For a wrong product,
              photograph the product you received alongside your order confirmation. Good evidence
              helps us resolve your request faster.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">Step 2 — Raise the Return Request on the Platform</h3>
            <ul className="list-disc space-y-2 pl-6">
              <li>Open the Havbit app or visit www.havbit.in and log in to your account</li>
              <li>Navigate to “My Orders” and find the relevant order</li>
              <li>Tap or click “Return / Refund Request” against the relevant product</li>
              <li>Select the return reason from the dropdown menu — choose the most accurate reason</li>
              <li>Upload your photographic or video evidence — this is mandatory for processing</li>
              <li>Add any additional comments that will help us understand the issue</li>
              <li>Submit your return request — you will receive an SMS and email acknowledgement immediately</li>
            </ul>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">Step 3 — Havbit Reviews Your Request</h3>
            <p>
              Once your return request is submitted, Havbit's customer support team will review it
              within 24 hours. The team will assess the return reason, review the evidence you have
              provided, and determine whether the request meets the eligibility criteria. If your
              evidence is unclear or insufficient, we may contact you for additional information or
              photographs. If the request is validated, we will notify you and proceed to the next step.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">Step 4 — Return Pickup (Where Applicable)</h3>
            <p>
              For most eligible return reasons, Havbit will arrange for a return pickup of the product
              from your delivery address. You will be notified of the scheduled pickup date and time by
              SMS and app notification. Please ensure:
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>The product is kept in its original outer packaging wherever possible</li>
              <li>All items that came with the product — including accessories, documentation, or packaging inserts — are included</li>
              <li>You or an authorised person is available at the delivery address during the scheduled pickup window</li>
              <li>For liquid or fragile products, ensure the product is adequately packed to prevent spillage or breakage during return transit</li>
            </ul>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">Step 5 — Refund or Replacement Processing</h3>
            <p>
              Once the return pickup is confirmed and the return is validated, Havbit will process your
              refund or replacement within the timelines described in Section 6 below.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">5. Products That Cannot Be Returned</h2>
            <p>
              The following products and circumstances are excluded from our standard return policy,
              primarily due to food safety, hygiene, and the perishable nature of food products:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>Products where the tamper-evident seal or primary packaging seal has been broken by you — unless the product was received with a broken seal, in which case this is an eligible return reason</li>
              <li>Products that have been opened and partially consumed — with the exception of situations where a food safety concern is identified after partial consumption</li>
              <li>Products returned more than 48 hours after confirmed delivery, except in genuine food safety cases</li>
              <li>Products that are returned without adequate photographic evidence of the defect</li>
              <li>Products where the product's condition upon return does not match the stated return reason — for example, a return cited as “damaged in transit” but where the product is found to be undamaged</li>
              <li>Customised or personalised food products made specifically to your requirements</li>
            </ul>
            <p className="mt-4">
              Exception for Food Safety: If you discover a serious food safety issue with a product —
              such as finding a foreign object, signs of contamination, or experiencing an adverse
              reaction — please contact us immediately at hello@havbit.in regardless of the 48-hour
              window. Your safety is our top priority and we will always investigate genuine safety
              concerns.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">6. Refund Process and Timelines</h2>

            <h3 className="mb-2 text-lg font-semibold text-gray-900">6.1 Full Refund</h3>
            <p>
              A full refund of the total amount paid for the affected product — including any shipping
              charges attributable to that product — will be processed in the following circumstances:
              the return request is validated and the return reason is confirmed; the seller fails to
              dispatch a replacement within the committed timeline; or the product is confirmed lost in
              transit by the courier partner.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">6.2 Partial Refund</h3>
            <p>
              A partial refund will be processed in the following circumstances: only some items in a
              multi-product order are defective or missing; the product has minor quality issues but is
              substantially as described; or a mutually agreed partial resolution is reached between
              you and Havbit.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">6.3 Refund Timeline by Payment Method</h3>

            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 text-left text-sm">
                <thead className="bg-red-50 text-gray-900">
                  <tr>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Payment Method</th>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Refund Timeline</th>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Refund Destination</th>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">How to Check Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">UPI — GPay, PhonePe, Paytm, etc.</td>
                    <td className="border border-gray-200 px-3 py-2">3–5 business days</td>
                    <td className="border border-gray-200 px-3 py-2">Original UPI account</td>
                    <td className="border border-gray-200 px-3 py-2">Card statement or bank</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Net Banking</td>
                    <td className="border border-gray-200 px-3 py-2">3–5 business days</td>
                    <td className="border border-gray-200 px-3 py-2">Source bank account</td>
                    <td className="border border-gray-200 px-3 py-2">My Orders → Refund Status</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Credit Card</td>
                    <td className="border border-gray-200 px-3 py-2">5–7 business days</td>
                    <td className="border border-gray-200 px-3 py-2">Credit card statement credit</td>
                    <td className="border border-gray-200 px-3 py-2">My Orders or bank statement</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Debit Card</td>
                    <td className="border border-gray-200 px-3 py-2">3–5 business days</td>
                    <td className="border border-gray-200 px-3 py-2">Source bank account</td>
                    <td className="border border-gray-200 px-3 py-2">My Orders → Refund Status</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Havbit Wallet Credit</td>
                    <td className="border border-gray-200 px-3 py-2">Within 24 hours</td>
                    <td className="border border-gray-200 px-3 py-2">Your Havbit Wallet</td>
                    <td className="border border-gray-200 px-3 py-2">Account → Wallet Balance</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Cash on Delivery (COD)</td>
                    <td className="border border-gray-200 px-3 py-2">5–7 business days</td>
                    <td className="border border-gray-200 px-3 py-2">NEFT to your bank account</td>
                    <td className="border border-gray-200 px-3 py-2">Email confirmation sent</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">No-Cost EMI</td>
                    <td className="border border-gray-200 px-3 py-2">5–7 business days</td>
                    <td className="border border-gray-200 px-3 py-2">Credit card — EMI cancelled</td>
                    <td className="border border-gray-200 px-3 py-2">My Orders → Refund Status</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="mt-4">
              Refund timelines start from the date Havbit confirms and initiates the refund, not
              from the date you raised the return request. The actual credit to your account depends
              on your bank's or payment gateway's processing times and may occasionally take longer
              than the stated timelines during high-volume periods.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">6.4 Refund to Havbit Wallet — Instant Option</h3>
            <p>
              For faster resolution, you may choose to receive your refund as Havbit Wallet credit
              instead of a bank refund. Wallet refunds are typically processed within 24 hours and can
              be immediately used for future purchases on the platform. Wallet refunds are optional and
              will only be applied if you select this option at the time of the return request.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">7. Replacement Policy</h2>
            <p>
              If you prefer a replacement over a refund, you may select this option when raising your
              return request. A replacement order will be dispatched by the seller within 24–48 hours of the return pickup being confirmed, subject to product availability at the time. The replacement will be shipped to the same delivery address as the original order at no additional cost to you.
            </p>
            <p className="mt-3">
              If the product is out of stock at the time of your replacement request, Havbit will notify you immediately. In such cases, you will be offered the choice of: waiting for the product to be restocked (timeline communicated by the seller); selecting an alternative product of equivalent value; or receiving a full refund to your original payment method. If you do not respond within 48 hours of notification, a full refund will be automatically processed.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">8. Checking Your Return and Refund Status</h2>
            <p>You can check the status of your return request and refund at any time through the following channels:</p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>Havbit App: Open the app → “My Orders” → select the relevant order → “Return / Refund Status”. This page shows real-time status updates at every stage of the process</li>
              <li>Havbit Website: Log in to www.havbit.in → “My Orders” → select the order → “Return / Refund Status”</li>
              <li>Email and SMS: You will receive automatic notifications at every key stage — request acknowledged, return pickup scheduled, refund initiated, and refund credited</li>
              <li>Customer Support: Email hello@havbit.in with your Order ID and return request reference number for a status update from our support team</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">9. Disputes and Escalations</h2>
            <p>
              If your return request is rejected and you believe the rejection is incorrect, or if you are not satisfied with the resolution provided, you have the following options:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>Request a review: Reply to the rejection notification email or contact us at hello@havbit.in with a clear explanation of why you believe the rejection is incorrect, and provide any additional evidence or information that supports your case</li>
              <li>Escalate to the Grievance Officer: If the review does not resolve your concern, you may escalate to our Grievance Officer by emailing hello@havbit.in with the subject line “Return Dispute Escalation — [Order ID]”. Our Grievance Officer will respond within 48 hours and resolve the dispute within 10 business days</li>
              <li>Consumer Forum: If you remain dissatisfied after all internal escalations, you may approach the appropriate Consumer Disputes Redressal Commission under the Consumer Protection Act, 2019 or call the National Consumer Helpline at 1800-11-4000</li>
            </ul>
          </section>

          <div className="border-t border-gray-200 pt-8 text-sm text-gray-700">
            <p className="font-semibold text-gray-900">Havbit Global Private Limited</p>
            <p>CIN: U63120HR2026PTC141507</p>
            <p>Innov8 UCP, 9th Floor, Tower D, Unitech Cyber Park, Gurgaon – 122001, Haryana, India</p>
            <p>Customer Support Email: hello@havbit.in</p>
            <p>Website: www.havbit.in</p>
            <p>Support Hours: Monday – Saturday, 10:00 AM – 6:00 PM IST</p>
            <p className="mt-4">
              By using the Havbit platform — whether by browsing, creating an account, or placing an
              order — you confirm that you have read, understood, and agree to be bound by this
              Return and Refund Policy and all related Havbit policies as updated from time to time.
            </p>
          </div>

          <div className="border-t border-gray-200 pt-6 text-center text-xs font-medium text-gray-600">
            © 2026 Havbit Global Private Limited | www.havbit.in | hello@havbit.in
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;